// src/pages/vendors/purchase-order-form-components/LineItemsSection.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField as MuiTextField,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  alpha,
  useTheme
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Inventory as InventoryIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { supabase } from '../../../supabaseClient';
import { useParams } from 'react-router-dom';

const LineItemsSection = ({ 
  lineItems, 
  inventoryItems, 
  locations, 
  onLineItemChange, 
  onAddLineItem, 
  onRemoveLineItem,
  canEdit = true 
}) => {
  const theme = useTheme();
  const { companyId } = useParams();
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [currentItemIndex, setCurrentItemIndex] = useState(null);
  const [currentItemType, setCurrentItemType] = useState('');
  const [itemSearchTerm, setItemSearchTerm] = useState('');
  const [availableItems, setAvailableItems] = useState([]);

  // Fetch items based on item type
  const fetchItems = async (itemType) => {
    if (!itemType) return [];

    try {
      let tableName = '';
      let selectFields = 'id, name, sku, unit_cost';
      let query = supabase.from('').select(selectFields);
      
      switch (itemType) {
        case 'bottles':
          tableName = 'bottles';
          query = supabase
            .from(tableName)
            .select(selectFields)
            .eq('company_id', companyId);
          break;
        case 'caps':
          tableName = 'caps';
          query = supabase
            .from(tableName)
            .select(selectFields)
            .eq('company_id', companyId);
          break;
        case 'labels':
          tableName = 'labels';
          query = supabase
            .from(tableName)
            .select(selectFields)
            .eq('company_id', companyId);
          break;
        case 'supplies':
          tableName = 'supplies';
          query = supabase
            .from(tableName)
            .select(selectFields)
            .eq('company_id', companyId);
          break;
        case 'chemicals':
          tableName = 'raw_materials';
          query = supabase
            .from(tableName)
            .select('id, name, sku, unit_cost, primary_unit_type, primary_unit_measure, primary_unit_quantity')
            .eq('company_id', companyId);
          break;
        case 'products':
          tableName = 'products';
          selectFields = 'id, name, sku, cost_price, unit_price';
          query = supabase
            .from(tableName)
            .select(selectFields)
            .eq('company_id', companyId)
            .eq('product_type', 'Private Label')
            .eq('is_active', true);
          break;
        default:
          return [];
      }

      const { data, error } = await query.order('name', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error(`Error fetching ${itemType}:`, err);
      return [];
    }
  };

  // Handle item type change
  const handleItemTypeChange = async (index, itemType) => {
    onLineItemChange(index, 'item_type', itemType);
    onLineItemChange(index, 'item_id', '');
    onLineItemChange(index, 'item_name', '');
    
    if (itemType) {
      const items = await fetchItems(itemType);
      setAvailableItems(items);
      setItemSearchTerm('');
      setCurrentItemIndex(index);
      setCurrentItemType(itemType);
      setItemDialogOpen(true);
    }
  };

  // Handle item selection
  const handleSelectItem = (item) => {
    if (currentItemIndex !== null) {
      onLineItemChange(currentItemIndex, 'item_id', item.id);
      onLineItemChange(currentItemIndex, 'item_name', item.name);
      const price = item.cost_price != null ? item.cost_price
        : item.unit_price != null ? item.unit_price
        : item.unit_cost ?? 0;
      if (price != null) onLineItemChange(currentItemIndex, 'unit_price', price);

      // Store unit metadata for description auto-fill
      if (currentItemType === 'chemicals') {
        const purchaseUnit = item.primary_unit_type    || '';
        const primaryUnit  = item.primary_unit_measure || '';
        const convFactor   = item.primary_unit_quantity || null;
        onLineItemChange(currentItemIndex, 'purchase_unit',         purchaseUnit);
        onLineItemChange(currentItemIndex, 'primary_unit',          primaryUnit);
        onLineItemChange(currentItemIndex, 'primary_unit_quantity', convFactor);
        // Auto-fill description with current qty
        // (qty not yet known here, default line item qty is 1)
        if (purchaseUnit) {
          const total = convFactor ? (1 * parseFloat(convFactor)).toLocaleString(undefined, { maximumFractionDigits: 3 }) : null;
          const desc = total ? `1 ${purchaseUnit} (${total} ${primaryUnit})` : `1 ${purchaseUnit}`;
          onLineItemChange(currentItemIndex, 'description', desc);
        }
      }

      setItemDialogOpen(false);
      setCurrentItemIndex(null);
      setCurrentItemType('');
      setItemSearchTerm('');
    }
  };

  const filteredItems = availableItems.filter(item =>
    item.name?.toLowerCase().includes(itemSearchTerm.toLowerCase()) ||
    item.sku?.toLowerCase().includes(itemSearchTerm.toLowerCase())
  );

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num || 0);
  };

  // Calculate total
  const totalAmount = lineItems
    .filter(item => item.item_type && item.item_id)
    .reduce((sum, item) => sum + (parseFloat(item.total_price) || 0), 0);

  return (
    <>
      <Paper sx={{ 
        p: 3, 
        mb: 3, 
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ 
            fontWeight: 500, 
            fontSize: '1.2rem',
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}>
            <InventoryIcon sx={{ fontSize: 28 }} />
            Line Items
          </Typography>
          {canEdit && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={onAddLineItem}
              sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                fontWeight: 500,
                '&:hover': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                }
              }}
            >
              Add Item
            </Button>
          )}
        </Box>

        <TableContainer sx={{ 
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: 'white',
          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.12)}`
        }}>
          <Table>
            <TableHead>
              <TableRow sx={{ 
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                '& .MuiTableCell-root': {
                  borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  fontWeight: 500,
                  fontSize: '0.8rem',
                  color: 'primary.main'
                }
              }}>
                <TableCell>Item Type</TableCell>
                <TableCell>Item Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="center" sx={{ width: 120 }}>Qty</TableCell>
                <TableCell align="right" sx={{ width: 120 }}>Unit Price</TableCell>
                <TableCell align="right" sx={{ width: 120 }}>Total</TableCell>
                <TableCell align="center" sx={{ width: 50 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lineItems.map((item, index) => (
                <TableRow key={item.id || index} sx={{
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.05)
                  }
                }}>
                  <TableCell>
                    <FormControl size="small" fullWidth disabled={!canEdit}>
                      <Select
                        value={item.item_type || ''}
                        onChange={(e) => handleItemTypeChange(index, e.target.value)}
                        displayEmpty
                        sx={{
                          borderRadius: 2,
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          background: 'white',
                          fontSize: '0.8rem',
                          '& .MuiOutlinedInput-notchedOutline': { border: 'none' }
                        }}
                      >
                        <MenuItem value="">Select Type</MenuItem>
                        <MenuItem value="chemicals">Chemicals</MenuItem>
                        <MenuItem value="caps">Caps</MenuItem>
                        <MenuItem value="bottles">Bottles</MenuItem>
                        <MenuItem value="labels">Labels</MenuItem>
                        <MenuItem value="supplies">Supplies</MenuItem>
                        <MenuItem value="products">Products</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        if (item.item_type && canEdit) {
                          fetchItems(item.item_type).then(items => {
                            setAvailableItems(items);
                            setCurrentItemIndex(index);
                            setCurrentItemType(item.item_type);
                            setItemDialogOpen(true);
                          });
                        }
                      }}
                      disabled={!item.item_type || !canEdit}
                      sx={{
                        borderRadius: 2,
                        fontSize: '0.8rem',
                        textTransform: 'none',
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        '&:hover': {
                          border: `1px solid ${theme.palette.primary.main}`
                        }
                      }}
                    >
                      {item.item_name || 'Select Item'}
                    </Button>
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      value={item.description || ''}
                      onChange={(e) => onLineItemChange(index, 'description', e.target.value)}
                      placeholder="Description"
                      disabled={!canEdit}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          fontSize: '0.8rem',
                          '& fieldset': {
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                          }
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      type="number"
                      size="small"
                      value={item.quantity || 1}
                      onChange={(e) => {
                        const newQty = e.target.value;
                        onLineItemChange(index, 'quantity', newQty);
                        // Auto-update description for chemicals
                        if (item.item_type === 'chemicals' && item.purchase_unit) {
                          const q = parseFloat(newQty) || 0;
                          const total = item.primary_unit_quantity
                            ? (q * parseFloat(item.primary_unit_quantity)).toLocaleString(undefined, { maximumFractionDigits: 3 })
                            : null;
                          const desc = total
                            ? `${q} ${item.purchase_unit} (${total} ${item.primary_unit || ''})`
                            : `${q} ${item.purchase_unit}`;
                          onLineItemChange(index, 'description', desc);
                        }
                      }}
                      inputProps={{ min: 1, step: 1 }}
                      disabled={!canEdit}
                      sx={{ width: 110, '& input': { textAlign: 'center', fontSize: '0.8rem' } }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      type="number"
                      size="small"
                      value={item.unit_price || 0}
                      onChange={(e) => onLineItemChange(index, 'unit_price', e.target.value)}
                      inputProps={{ min: 0, step: 0.01 }}
                      disabled={!canEdit}
                      sx={{ width: 100, '& input': { textAlign: 'right', fontSize: '0.8rem' } }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                      {formatCurrency(item.total_price || 0)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {canEdit && (
                      <IconButton
                        size="small"
                        onClick={() => onRemoveLineItem(index)}
                        sx={{
                          color: 'error.main',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.error.main, 0.1)
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Total Amount */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Box sx={{ width: 300 }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              borderTop: '2px solid #000', 
              pt: 2, 
              mt: 2 
            }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Total:
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {formatCurrency(totalAmount)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Item Selection Dialog */}
      <Dialog
        open={itemDialogOpen}
        onClose={() => {
          setItemDialogOpen(false);
          setItemSearchTerm('');
          setCurrentItemIndex(null);
          setCurrentItemType('');
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 500, fontSize: '1.2rem' }}>
          Select Item
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Search items..."
            value={itemSearchTerm}
            onChange={(e) => setItemSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              sx: { borderRadius: 2, mb: 2 }
            }}
          />
          <List>
            {filteredItems.length === 0 ? (
              <ListItem>
                <ListItemText 
                  primary={currentItemType === 'products' 
                    ? "You have no private labeled products" 
                    : "No items found"} 
                />
              </ListItem>
            ) : (
              filteredItems.map((item) => (
                <ListItem key={item.id} disablePadding>
                  <ListItemButton
                    onClick={() => handleSelectItem(item)}
                    sx={{
                      borderRadius: 2,
                      mb: 0.5,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.1)
                      }
                    }}
                  >
                    <ListItemText
                      primary={item.name}
                      secondary={item.sku}
                    />
                  </ListItemButton>
                </ListItem>
              ))
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setItemDialogOpen(false);
            setItemSearchTerm('');
            setCurrentItemIndex(null);
            setCurrentItemType('');
          }}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LineItemsSection;